import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ScrEnvironment } from 'src/environments/environment';
import { EditUserRequest, GetUserProfileResponse, GetUsersResponse, UpdateUserProfileRequest, User } from '../models/user.model';
import { WITHCRED, WithCredNoCache } from './authentication.service';
import { ScrServiceManager } from './servicemanager.service';
import { ScrBaseResponse, ScrBaseService } from './base.service';
import { CacheService } from './cache.service';
import { tap } from 'rxjs/operators';
import { CACHEKEY_USER } from '../shared/constants';

@Injectable({
  providedIn: 'root'
})
export class UserService extends ScrBaseService {

  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    private cache: CacheService,
    private http: HttpClient) {
    super(serviceManager);
  }

  getUserProfile(clearcache = false): Observable<GetUserProfileResponse> {
    if (clearcache) {
      this.cache.invalidate(CACHEKEY_USER);
    }

    const u = this.cache.get<User>(CACHEKEY_USER);
    if (u) {
      const user = Object.assign({}, u);
      return of(new GetUserProfileResponse(user, 'success', []));
    }
    const url = `${this.environment.base}/secure/userprofile`;
    return this.http.get<GetUserProfileResponse>(url, WithCredNoCache).pipe(tap(rsp => {
      if (rsp.status === 'success' && rsp.user) {
        this.cache.set(CACHEKEY_USER, rsp.user);
      }
    }));
  }

  updateUserProfile(user: User): Observable<UpdateUserProfileRequest> {
    const url = `${this.environment.base}/secure/userprofile`;
    const reqBody = JSON.stringify({user}, ScrBaseService.phoneReplacer);
    this.cache.set(CACHEKEY_USER, undefined); // clear any cached value.
    return this.http.put<UpdateUserProfileRequest>(url, reqBody, WITHCRED).pipe(tap(rsp => {
      if (rsp.status === 'success') {
        this.cache.invalidate(CACHEKEY_USER);
      }
    }));
  }

  addUserProfile(user: User): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/usermanagement`;
    const userBody = JSON.stringify(user, ScrBaseService.phoneReplacer);
    return this.http.post<ScrBaseResponse>(url, userBody, WITHCRED);
  }

  editUserProfile(user: User): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/usermanagement`;
    const userBody = JSON.stringify(user, ScrBaseService.phoneReplacer);
    return this.http.put<ScrBaseResponse>(url, userBody, WITHCRED);
  }

  editStatusUser(user: EditUserRequest): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/usermanagement/status`;
    return this.http.put<ScrBaseResponse>(url, user, WITHCRED);
  }

  getUsers(): Observable<GetUsersResponse> {
    const url = `${this.environment.base}/secure/usermanagement`;
    return this.http.get<GetUsersResponse>(url, WithCredNoCache);
  }

  deletePendingUser(userId: number): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/usermanagement/users/${userId}`;
    return this.http.delete<ScrBaseResponse>(url, WITHCRED);
  }

  public resendEmail(userId: string) {
    const url = `${this.environment.base}/secure/usermanagement/resendemail/${userId}`;
    return this.http.post<ScrBaseResponse>(url, {}, WITHCRED);
  }
}
